import * as Sentry from '@sentry/react';
import { put, select } from 'redux-saga/effects';
import { create, getAccess, update } from 'api/cloudsave/cloudsaveApi';
import { getCurrentUserId } from 'auth/userIdentity';
import {
  errorMessage,
  storageUnitCreated,
} from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { addFileEndingIfMissing } from 'util/file';
import { routes } from 'util/routes';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import {
  createStorageUnitFailure,
  createStorageUnitRequested,
  createStorageUnitSuccess,
} from './reducer';
import type { CreateStorageUnitRequestActionInterface } from './sagaActions';
import type { AccessRead, StorageUnit } from './types';
import { StorageUnitType } from './types';
import { MessageType } from 'store/snackbar/types';
import { mergeServerStorageUnitWithSubmittedContent } from './utils';

export function* createStorageUnit(
  action: CreateStorageUnitRequestActionInterface,
) {
  const {
    tempId,
    newStorageUnit,
    openAfterCreate,
    isUpload,
    navigate,
    undoable,
  } = action.payload;
  const currentUserId = getCurrentUserId();

  yield put(
    createStorageUnitRequested({
      tempId,
      name: newStorageUnit.name,
      fileData: newStorageUnit.fileData,
      parentId: newStorageUnit.parentId,
      status: newStorageUnit.status,
      isUpload,
      projectMetadata: newStorageUnit.projectMetadata,
      flowsheetMetadata: newStorageUnit.flowsheetMetadata,
      ownerUserId: currentUserId,
    }),
  );

  const isOnline: boolean = yield select(getIsOnline);

  if (!isOnline) {
    const now = new Date();
    const nowIso = now.toISOString();
    const offlineUnit: StorageUnit = {
      id: tempId,
      name:
        newStorageUnit.type === StorageUnitType.File
          ? addFileEndingIfMissing(newStorageUnit.name)
          : newStorageUnit.name,
      parentId: newStorageUnit.parentId,
      type: newStorageUnit.type,
      fileData: newStorageUnit.fileData,
      thumbnail: newStorageUnit.thumbnail,
      thumbnailDark: newStorageUnit.thumbnailDark,
      ...(newStorageUnit.projectMetadata
        ? { project: newStorageUnit.projectMetadata }
        : {}),
      ...(newStorageUnit.flowsheetMetadata
        ? { flowsheetMetadata: newStorageUnit.flowsheetMetadata }
        : {}),
      createdAt: now,
      updatedAt: now,
      exclusiveLease: undefined,
      softLeases: [],
      access: {
        direct: currentUserId ? [currentUserId] : [],
        indirect: [],
      },
      status: newStorageUnit.status ?? [],
      archived: false,
      isSynced: false,
      syncedAt: null,
      localUpdatedAt: nowIso,
      pendingAction: 'create',
    };

    const normalized: StorageUnit = yield storageUnitCache.setOne(offlineUnit, {
      markSynced: false,
      pendingAction: 'create',
      localUpdatedAt: nowIso,
    });

    yield put(createStorageUnitSuccess({ tempId, storageUnit: normalized }));
    yield put(
      addMessage(
        storageUnitCreated(
          normalized,
          openAfterCreate ? MessageType.SAVE_AS : MessageType.CREATED,
          { undoable },
        ),
      ),
    );

    if (openAfterCreate && navigate) {
      void navigate(routes.file(normalized.id));
    }

    return;
  }

  try {
    const { parsedBody } = yield withRetry(() => create(newStorageUnit));
    let storageUnit: StorageUnit = parsedBody;

    storageUnit = mergeServerStorageUnitWithSubmittedContent(
      storageUnit,
      newStorageUnit,
    );

    if (newStorageUnit.flowsheetMetadata) {
      const { parsedBody: updatedStorageUnit }: { parsedBody: StorageUnit } =
        yield withRetry(() =>
          update({
            id: storageUnit.id,
            type: StorageUnitType.File,
            flowsheetMetadata: newStorageUnit.flowsheetMetadata,
          }),
        );
      storageUnit = mergeServerStorageUnitWithSubmittedContent(
        updatedStorageUnit,
        storageUnit,
      );
    }

    try {
      const { parsedBody: access }: { parsedBody: AccessRead } =
        yield withRetry(() => getAccess(parsedBody.id));
      storageUnit = {
        ...storageUnit,
        softLeases: storageUnit.softLeases ?? [],
        access: {
          indirect: access.indirect.map((u) => u.id),
          direct: access.direct.map((u) => u.id),
        },
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { saga: 'createStorageUnit', step: 'getAccess' },
        extra: { storageUnitId: parsedBody.id },
        level: 'warning',
      });
    }

    const normalized: StorageUnit = yield storageUnitCache.setOne(storageUnit);
    yield storageUnitCache.clearList();

    yield put(createStorageUnitSuccess({ tempId, storageUnit: normalized }));
    yield put(
      addMessage(
        storageUnitCreated(
          normalized,
          openAfterCreate ? MessageType.SAVE_AS : MessageType.CREATED,
          { undoable },
        ),
      ),
    );

    if (openAfterCreate && navigate) {
      void navigate(routes.file(normalized.id));
    }
  } catch (err) {
    const payloadSize = JSON.stringify(newStorageUnit).length;
    const fileDataSize = newStorageUnit.fileData?.length ?? 0;

    Sentry.captureException(err, {
      tags: { saga: 'createStorageUnit' },
      extra: {
        tempId,
        storageUnitType: newStorageUnit.type,
        payloadSize,
        fileDataSize,
        hasFileData: !!newStorageUnit.fileData,
      },
    });
    yield put(
      addMessage(
        errorMessage(
          `Couldn't create ${newStorageUnit.type}. Please try again later.`,
        ),
      ),
    );
    yield put(createStorageUnitFailure(tempId));
  }
}
